"use client";

import { Basket } from "@features/cartDetails/types";
import { EventData, EventShowing, EventShowingsMsgs } from "@types";
import { AccessType } from "@utils/enums";
import { getCookieConsent, getCookieValue } from "@utils/helpers";
import { useIsInViewport } from "@utils/hooks";
import { format, parseISO } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { KeyedMutator } from "swr";
import { EventAreaPicker } from "../../eventAreaPicker";
import { TimesType } from "@features/eventPage/types";
import EventDatePicker from "./eventDatePicker";
import EventTimePicker from "./eventTimePicker";
import { Card, Heading } from "@components/theming";
import { useTranslations } from "next-intl";
import { formatTime, handleAccessType } from "../utils/helpers";
import { useRequiresCookieConsent } from "@context/DomainContext";
import { AreaFormatted } from "@features/eventAreaPicker/types";

interface Props {
  showingData: EventShowingsMsgs;
  basket: Basket | undefined;
  event: EventData;
  getPriceRange: ({
    pricesectionIds,
    groupedPricesectionIds,
  }: {
    pricesectionIds?: number[];
    groupedPricesectionIds?: number[];
  }) => { min: number; max: number; isEmpty?: boolean };
  mutate: KeyedMutator<EventShowingsMsgs>;
}

const Showings = ({
  showingData,
  basket,
  event,
  getPriceRange,
  mutate,
}: Props) => {
  const eventId = event.legacy.e_id;
  const eventVenueId = event.legacy.ev_id;
  const [times, setTimes] = useState<TimesType[]>([]);
  const [selectedShowingTime, setSelectedShowingTime] = useState<
    EventShowing | undefined
  >(undefined);
  const [lastAreaSelected, setLastAreaSelected] =
    useState<AreaFormatted | null>(null);
  const { isInViewport, scrollToTarget, targetRef } =
    useIsInViewport<HTMLHeadingElement>();
  const [requestingBasket, setRequestingBasket] = useState(false);

  const t = useTranslations("EventPage");
  const statusTranslations = useTranslations("EventPage.statuses");
  // if user does not consent to cookies, don't pass analytics configs down to children
  // so analytics calls won't accidentally be triggered
  const cookieConsentRequired = useRequiresCookieConsent();
  const consent = getCookieConsent();

  const basketEvent = basket?.Events?.find((event) => event.Id === eventId);

  async function createBasket() {
    const basketKey = getCookieValue("ASP.NET_SessionId");
    if (!requestingBasket && !basketKey) {
      setRequestingBasket(true);
      const session_params = getCookieValue("itk_sessionparams") ?? "";
      const res = await fetch(`/api/basket`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(session_params),
      });
      if (!res.ok) {
        const retry_after = res.headers.get("retry-after") ?? "";
        setTimeout(() => createBasket(), parseInt(retry_after) * 1000);
      }
      setRequestingBasket(!res.ok);
    }
  }

  const bookedTimeTickets = (id: number) =>
    basketEvent?.Showings?.filter((showing) => showing.Id === id).map(
      (showing) => showing.Items.length
    )[0];

  const bookedDateTickets = (id: number): number | undefined => {
    const selectedDate = showingData?.showings.find((ud) => ud.id === id);

    if (!selectedDate) return;

    return showingData?.showings
      .filter(
        (s) =>
          s.active &&
          format(parseISO(s.date.local.start), `d MMM`) ===
            format(parseISO(selectedDate.date.local.start ?? ""), `d MMM`)
      )
      .map((v) =>
        basketEvent?.Showings?.filter((showing) => showing.Id === v.id)
          .map((showing) => showing.Items.length)
          .reduce((prev, curr) => prev + curr, 0)
      )
      .reduce((prev: any, curr) => {
        return prev + curr;
      }, 0);
  };

  const handleSelectDate = useCallback(
    (id: number | null) => {
      if (!showingData) return;
      setLastAreaSelected(null);
      if (!id) {
        setSelectedShowingTime(undefined);
        setTimes([]);
        return;
      }
      const selectedDate = showingData?.showings.find((ud) => ud.id === id);
      setSelectedShowingTime(undefined);

      const times = showingData?.showings
        .filter(
          (s) =>
            s.active &&
            format(parseISO(s.date.local.start), `d MMM`) ===
              format(parseISO(selectedDate?.date.local.start ?? ""), `d MMM`)
        )
        .map((s) => ({
          id: s.id,
          name: format(parseISO(s.date.local.start), `h:mm aa`),
          displayDate: s.date.display,
          displayName: s.name,
          formattedDate: s.date.local.formatted,
          seconds:
            s.access.type === AccessType.PASSWORD
              ? s.access.date.password.secondstostart
              : s.access.date.public.secondstostart,
          startDate: s.access.date.public.local.start.formatted,
          availability: handleAccessType(
            s,
            showingData.messages,
            statusTranslations
          ),
          accessType: s.access.type,
          statusId: s.status.id,
        }));

      if (!isInViewport) {
        scrollToTarget();
      }

      const sortedTimes = times.sort((a, b) => {
        // Convert the time strings to a common format for comparison.
        const formattedA = formatTime(a.name);
        const formattedB = formatTime(b.name);

        // Compare hours first.
        if (formattedA.isPM !== formattedB.isPM) {
          return formattedA.isPM ? 1 : -1;
        }

        if (formattedA.hour !== formattedB.hour) {
          return formattedA.hour - formattedB.hour;
        }

        // If hours are the same, compare minutes.
        return formattedA.minute - formattedB.minute;
      });

      setTimes(sortedTimes);
      if (times.length === 1 && times[0].availability?.selectable) {
        const foundTime = showingData?.showings.find(
          (ud) => ud.id === times[0].id
        );
        setSelectedShowingTime(foundTime);
      }
    },
    [showingData, isInViewport, scrollToTarget, statusTranslations]
  );

  const handleSelectTime = (id: number | null) => {
    setLastAreaSelected(null);
    if (!id) {
      setSelectedShowingTime(undefined);
      return;
    }
    const foundTime = showingData?.showings.find((ud) => ud.id === id);

    setSelectedShowingTime(foundTime);
  };

  useEffect(() => {
    const uniqueShowings = Object.values(
      (showingData.showings as EventShowing[]).reduce(
        (acc, obj) => ({
          ...acc,
          [format(parseISO(obj.date.local.start), `d MMM yyyy`)]: obj,
        }),
        {} as EventShowing[]
      )
    );
    if (uniqueShowings.length === 1) {
      handleSelectDate(uniqueShowings[0].id);
    }
  }, [handleSelectDate, showingData.showings]);

  return (
    <motion.div
      className="mt-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Heading
        as="h2"
        asChild
        size="4"
        weight="bold"
        shopsiteClasses="text-lg font-semibold"
      >
        <motion.h2
          initial={
            showingData.showings.length === 1
              ? { opacity: 0, height: 0 }
              : { opacity: 1, height: 44 }
          }
          animate={
            times.length > 0
              ? { opacity: 0, height: 0 }
              : { opacity: 1, height: 44 }
          }
        >
          {t("calendarmode.chooseadate")}
        </motion.h2>
      </Heading>
      <EventDatePicker
        showings={showingData.showings}
        messages={showingData.messages}
        bookedDateTickets={bookedDateTickets}
        handleSelectDate={handleSelectDate}
      />
      <AnimatePresence>
        {times.length > 0 && !selectedShowingTime && (
          <Heading
            as="h2"
            asChild
            size="4"
            weight="bold"
            sharedClasses={selectedShowingTime ? "hidden" : ""}
            shopsiteClasses="text-lg font-semibold"
          >
            <motion.h2
              ref={targetRef}
              key="chooseatime"
              initial="hide"
              animate="show"
              exit="hide"
              variants={{
                hide: { opacity: 0, height: 0 },
                show: { opacity: 1, height: 44 },
              }}
            >
              {t("calendarmode.chooseatime")}
            </motion.h2>
          </Heading>
        )}
      </AnimatePresence>
      <EventTimePicker
        bookedTimeTickets={bookedTimeTickets}
        times={times}
        selectedTime={selectedShowingTime}
        handleSelectTime={handleSelectTime}
        mutate={mutate}
      />
      <AnimatePresence>
        {!!(times.length > 0 && selectedShowingTime) && (
          <motion.div
            initial="hide"
            animate="show"
            exit="hide"
            variants={{
              hide: {
                opacity: 0,
                height: 0,
              },
              show: {
                opacity: 1,
                height: "auto",
                transition: {
                  opacity: {
                    delay: 0.2,
                  },
                },
              },
            }}
          >
            <div className="mb-1">
              {requestingBasket ? (
                <Card>
                  <div className="flex flex-col items-center justify-center w-full h-[197px]">
                    <div className="relative mx-4">
                      <Heading
                        as="h3"
                        size="6"
                        weight="bold"
                        align="center"
                        sharedClasses="relative z-10"
                        shopsiteClasses="text-xl font-semibold text-gray-600"
                      >
                        {t("error.retrymessage")}
                      </Heading>
                    </div>
                  </div>
                </Card>
              ) : (
                <EventAreaPicker
                  key={selectedShowingTime.id}
                  currency={event.currency}
                  showing={selectedShowingTime}
                  messages={showingData.messages}
                  eventId={eventId}
                  eventName={event.name}
                  eventVenueId={eventVenueId}
                  orgUid={event.organisation.uid}
                  basketEvent={basketEvent}
                  getPriceRange={getPriceRange}
                  selectedConnectedShowings={null}
                  pricesection={
                    event.pricing.priceschemes?.find(
                      (ps) =>
                        ps.id === selectedShowingTime.config.pricescheme.id
                    )?.pricesections
                  }
                  lastAreaSelected={lastAreaSelected}
                  setLastAreaSelected={setLastAreaSelected}
                  createBasket={createBasket}
                  analyticsConfigs={
                    event.analyticsConfigs &&
                    (!cookieConsentRequired || consent.marketing === true)
                      ? event.analyticsConfigs
                      : []
                  }
                  landingPages={event.landingpages}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Showings;
